/**
 * AI chatbot - shared shell with two engine backends, two surfaces.
 *
 *   engine: 'algolia'  - NAM/MLC/MI: streams directly from Algolia Agent
 *                        Studio (POST /agent-studio/1/agents/{id}/completions
 *                        with ai-sdk-5 SSE), rendered in our custom chat UI.
 *   engine: 'betty'    - IRI: mounts the Tasio <betty-bot> web component.
 *                        Suggestion chips bridge to Betty via a shadow-DOM
 *                        textarea injection.
 *
 * Surfaces:
 *   floating  - #nam-ai-chatbot-root in wp_footer. Toggle button + modal,
 *               conversation persisted across page navigation (algolia).
 *   inline    - .nam-ai-chatbot-inline blocks embedded in page content.
 *               Always-visible chat panel rendered into the block's
 *               .nam-ai-chatbot-inline-mount. An editor-set "pre-typed
 *               prompt" pre-fills the input (the visitor sends it). Inline
 *               instances do not persist to sessionStorage.
 *
 * Page-aware suggestion chips are fetched from
 * /wp-json/nam/v1/ai-suggestions/<post_id> and generated server-side.
 */
(function () {
	'use strict';

	var FLOATING_ROOT_ID = 'nam-ai-chatbot-root';
	var INLINE_SELECTOR = '.nam-ai-chatbot-inline';
	var INLINE_MOUNT_SELECTOR = '.nam-ai-chatbot-inline-mount';
	var BODY_HAS_CLASS = 'has-ai-chatbot';
	var BODY_OPEN_CLASS = 'ai-chatbot-open';

	var STORAGE_KEY = 'nam-aichat-state';
	var STORAGE_VERSION = 1;
	var MAX_HISTORY_MESSAGES = 30;

	// --------------------------------------------------------------
	// Shared pure helpers (no per-instance state)
	// --------------------------------------------------------------

	function genId() {
		if ( typeof crypto !== 'undefined' && crypto.randomUUID ) {
			return 'aichat-' + crypto.randomUUID();
		}
		return 'aichat-' + Date.now().toString( 36 ) + '-' + Math.random().toString( 36 ).slice( 2 );
	}

	function escapeHtml( s ) {
		return String( s )
			.replace( /&/g, '&amp;' )
			.replace( /</g, '&lt;' )
			.replace( />/g, '&gt;' )
			.replace( /"/g, '&quot;' )
			.replace( /'/g, '&#39;' );
	}

	// Minimal, safe Markdown -> HTML renderer scoped to what an LLM
	// commonly emits in chat answers.
	function renderMarkdown( raw ) {
		var s = String( raw || '' );
		if ( ! s.trim() ) { return ''; }

		s = escapeHtml( s );

		var codeBlocks = [];
		s = s.replace( /```([\s\S]*?)```/g, function ( _m, code ) {
			var idx = codeBlocks.length;
			codeBlocks.push( '<pre><code>' + code.replace( /^\n+|\n+$/g, '' ) + '</code></pre>' );
			return '\u0000CB' + idx + '\u0000';
		} );
		var inlineCodes = [];
		s = s.replace( /`([^`\n]+?)`/g, function ( _m, code ) {
			var idx = inlineCodes.length;
			inlineCodes.push( '<code>' + code + '</code>' );
			return '\u0000IC' + idx + '\u0000';
		} );

		s = s.replace( /^#####\s+(.+?)\s*#*\s*$/gm, '<h5>$1</h5>' );
		s = s.replace( /^####\s+(.+?)\s*#*\s*$/gm, '<h4>$1</h4>' );
		s = s.replace( /^###\s+(.+?)\s*#*\s*$/gm, '<h3>$1</h3>' );
		s = s.replace( /^##\s+(.+?)\s*#*\s*$/gm, '<h2>$1</h2>' );
		s = s.replace( /^#\s+(.+?)\s*#*\s*$/gm, '<h2>$1</h2>' );

		s = s.replace( /\*\*([^\*\n]+?)\*\*/g, '<strong>$1</strong>' );
		s = s.replace( /__([^_\n]+?)__/g, '<strong>$1</strong>' );

		s = s.replace( /\*([^\*\n]+?)\*/g, '<em>$1</em>' );

		s = s.replace( /\[([^\]\n]+)\]\(([^)\s]+?)\)/g, function ( _m, label, url ) {
			if ( ! /^(https?:\/\/|mailto:|#|\/)/i.test( url ) ) {
				return label;
			}
			return '<a href="' + url + '" target="_blank" rel="noopener noreferrer">' + label + '</a>';
		} );

		// Angle-bracket autolinks: <https://example.com> / <mailto:a@b.co>.
		// escapeHtml() already turned the brackets into &lt; / &gt;.
		s = s.replace( /&lt;((?:https?:\/\/|mailto:)[^\s<>]+?)&gt;/gi, function ( _m, url ) {
			return '<a href="' + url + '" target="_blank" rel="noopener noreferrer">' + url + '</a>';
		} );

		// Bare URLs, trimming common trailing punctuation so a sentence-final
		// period/comma is not swallowed into the link.
		s = s.replace( /(^|[\s(])(https?:\/\/[^\s<>"')]+)/g, function ( _m, pre, url ) {
			var trail = '';
			var mt = url.match( /[.,;:!?]+$/ );
			if ( mt ) { trail = mt[ 0 ]; url = url.slice( 0, -trail.length ); }
			return pre + '<a href="' + url + '" target="_blank" rel="noopener noreferrer">' + url + '</a>' + trail;
		} );

		var lines = s.split( '\n' );
		var out = [];
		var inUl = false, inOl = false;
		function closeLists() {
			if ( inUl ) { out.push( '</ul>' ); inUl = false; }
			if ( inOl ) { out.push( '</ol>' ); inOl = false; }
		}
		for ( var i = 0; i < lines.length; i++ ) {
			var line = lines[ i ];
			var ul = line.match( /^[\-\*]\s+(.+)$/ );
			var ol = line.match( /^\d+\.\s+(.+)$/ );
			if ( ul ) {
				if ( inOl ) { out.push( '</ol>' ); inOl = false; }
				if ( ! inUl ) { out.push( '<ul>' ); inUl = true; }
				out.push( '<li>' + ul[ 1 ] + '</li>' );
			} else if ( ol ) {
				if ( inUl ) { out.push( '</ul>' ); inUl = false; }
				if ( ! inOl ) { out.push( '<ol>' ); inOl = true; }
				out.push( '<li>' + ol[ 1 ] + '</li>' );
			} else {
				closeLists();
				out.push( line );
			}
		}
		closeLists();
		s = out.join( '\n' );

		var blocks = s.split( /\n{2,}/ ).map( function ( block ) {
			var trimmed = block.trim();
			if ( ! trimmed ) { return ''; }
			if ( /^<(h[1-6]|ul|ol|pre|p|blockquote)/i.test( trimmed ) ) {
				return trimmed;
			}
			return '<p>' + trimmed.replace( /\n/g, '<br>' ) + '</p>';
		} ).filter( Boolean );
		s = blocks.join( '\n' );

		inlineCodes.forEach( function ( code, idx ) {
			s = s.split( '\u0000IC' + idx + '\u0000' ).join( code );
		} );
		codeBlocks.forEach( function ( block, idx ) {
			s = s.split( '\u0000CB' + idx + '\u0000' ).join( block );
		} );

		return s;
	}

	// Some agents return their answer as raw HTML (occasionally a whole
	// <html><body>...</body></html> document) instead of Markdown. Render
	// that HTML through a strict allowlist sanitizer so it displays as
	// formatted text rather than escaped tags. Everything else goes
	// through the Markdown renderer.
	var SANITIZE_ALLOWED_TAGS = {
		A: 1, P: 1, BR: 1, STRONG: 1, B: 1, EM: 1, I: 1, U: 1,
		UL: 1, OL: 1, LI: 1, H2: 1, H3: 1, H4: 1, H5: 1, H6: 1,
		CODE: 1, PRE: 1, BLOCKQUOTE: 1, HR: 1
	};
	var SANITIZE_DROP_TAGS = {
		SCRIPT: 1, STYLE: 1, HEAD: 1, TITLE: 1, META: 1, LINK: 1,
		IFRAME: 1, OBJECT: 1, EMBED: 1, NOSCRIPT: 1, FORM: 1, INPUT: 1
	};

	// If the whole message is a single fenced code block of HTML, unwrap
	// it so the markup renders instead of showing the literal fence.
	function stripHtmlCodeFence( s ) {
		var t = String( s || '' ).trim();
		var m = t.match( /^```(?:html)?\s*\n?([\s\S]*?)\n?```$/i );
		if ( m && /<[a-z!/]/i.test( m[ 1 ] ) ) { return m[ 1 ]; }
		return s;
	}

	function looksLikeHtml( s ) {
		return /<(?:\/?\s*)(?:html|body|p|br|ul|ol|li|h[1-6]|strong|em|b|i|a|table|tr|td|th|thead|tbody|blockquote|div|span|pre|code)\b[^>]*>/i.test( String( s || '' ) );
	}

	// Append text to a node, turning bare and <angle-bracket> URLs into
	// clickable links. Skipped when already inside an <a>.
	function appendLinkified( dest, rawText ) {
		var text = String( rawText || '' );
		var re = /<((?:https?:\/\/|mailto:)[^\s<>]+)>|(https?:\/\/[^\s<>"')]+)/g;
		var last = 0;
		var m;
		while ( ( m = re.exec( text ) ) ) {
			if ( m.index > last ) {
				dest.appendChild( document.createTextNode( text.slice( last, m.index ) ) );
			}
			var url = m[ 1 ] || m[ 2 ];
			var trail = '';
			if ( ! m[ 1 ] ) {
				var mt = url.match( /[.,;:!?)\]]+$/ );
				if ( mt ) { trail = mt[ 0 ]; url = url.slice( 0, -trail.length ); }
			}
			var a = document.createElement( 'a' );
			a.setAttribute( 'href', url );
			a.setAttribute( 'target', '_blank' );
			a.setAttribute( 'rel', 'noopener noreferrer' );
			a.textContent = url;
			dest.appendChild( a );
			if ( trail ) { dest.appendChild( document.createTextNode( trail ) ); }
			last = m.index + m[ 0 ].length;
		}
		if ( last < text.length ) {
			dest.appendChild( document.createTextNode( text.slice( last ) ) );
		}
	}

	function sanitizeHtmlInto( srcParent, destParent, insideLink ) {
		var children = srcParent.childNodes;
		for ( var i = 0; i < children.length; i++ ) {
			var node = children[ i ];
			if ( node.nodeType === 3 ) {
				if ( insideLink ) {
					destParent.appendChild( document.createTextNode( node.nodeValue ) );
				} else {
					appendLinkified( destParent, node.nodeValue );
				}
				continue;
			}
			if ( node.nodeType !== 1 ) { continue; }

			var tag = node.tagName;
			if ( SANITIZE_DROP_TAGS[ tag ] ) { continue; }

			if ( SANITIZE_ALLOWED_TAGS[ tag ] ) {
				var el = document.createElement( tag.toLowerCase() );
				if ( tag === 'A' ) {
					var href = node.getAttribute( 'href' ) || '';
					if ( /^(https?:\/\/|mailto:|#|\/)/i.test( href ) ) {
						el.setAttribute( 'href', href );
						el.setAttribute( 'target', '_blank' );
						el.setAttribute( 'rel', 'noopener noreferrer' );
					}
				}
				sanitizeHtmlInto( node, el, insideLink || tag === 'A' );
				destParent.appendChild( el );
			} else {
				// Unwrap unknown/structural tags (html, body, div, span,
				// table, ...) but keep their (sanitized) children.
				sanitizeHtmlInto( node, destParent, insideLink );
			}
		}
	}

	function sanitizeHtml( html ) {
		var doc;
		try {
			doc = new DOMParser().parseFromString( String( html || '' ), 'text/html' );
		} catch ( e ) {
			return '';
		}
		var src = ( doc && doc.body ) ? doc.body : doc;
		if ( ! src ) { return ''; }
		var dest = document.createElement( 'div' );
		sanitizeHtmlInto( src, dest, false );
		return dest.innerHTML;
	}

	function formatAssistantText( text ) {
		var raw = String( text || '' );
		var unfenced = stripHtmlCodeFence( raw );
		if ( looksLikeHtml( unfenced ) ) {
			var safe = sanitizeHtml( unfenced );
			if ( safe && safe.trim() ) { return safe; }
		}
		return renderMarkdown( raw );
	}

	// --------------------------------------------------------------
	// Per-instance boot. `root` is the element the engine renders into
	// and scopes all queries to. For floating it is #nam-ai-chatbot-root;
	// for inline it is the block's .nam-ai-chatbot-inline-mount.
	// --------------------------------------------------------------

	function bootInstance( root, config, options ) {
		options = options || {};
		// surface: 'floating' (wp_footer widget) | 'inline' (always-open
		// block shell) | 'archive-aside' (block that starts minimized then
		// self-unfurls over the archive aside panel).
		var surface = options.surface || ( options.inline ? 'inline' : 'floating' );
		var isInlineShell = ( surface === 'inline' );
		var isFloatingChrome = ( surface !== 'inline' );
		var isArchiveAside = ( surface === 'archive-aside' );
		var isBlock = ( surface !== 'floating' );
		// Back-compat alias used by a few engine-local checks below.
		var inline = isInlineShell;
		var prefillPrompt = options.prompt || '';

		if ( ! root || ! config ) { return; }

		var engine = config.engine || 'algolia';

		// Engine-specific gate checks - bail silently if it cannot run.
		if ( engine === 'betty' ) {
			if ( ! config.betty || ! config.betty.apiUrl || ! config.betty.apiToken ) {
				console.warn( '[ai-chatbot] betty engine missing apiUrl/apiToken; disabled' );
				return;
			}
		} else {
			if ( ! config.agentId ) {
				console.warn( '[ai-chatbot] no agentId configured; disabled' );
				return;
			}
			if ( ! config.algolia || ! config.algolia.appId || ! config.algolia.searchKey ) {
				console.warn( '[ai-chatbot] missing Algolia appId/searchKey; disabled', config.algolia );
				return;
			}
		}

		if ( surface === 'floating' ) {
			document.body.classList.add( BODY_HAS_CLASS );
			root.removeAttribute( 'hidden' );
			window.namAIChatbotContext = config.pageContext || null;
		} else if ( isArchiveAside ) {
			// Block-spawned floating widget: mark its host so the fixed
			// bottom-right positioning + betty styling apply.
			document.body.classList.add( BODY_HAS_CLASS );
			root.classList.add( 'nam-ai-chatbot-archive-host' );
			root.setAttribute( 'data-engine', engine );
		}

		function buildToggleInner() {
			var toggleStyle = config.toggleStyle || 'ask_label';

			if ( toggleStyle === 'avatar' && config.toggleImageUrl ) {
				return '<img class="nam-aichat-toggle-avatar" src="' +
					escapeHtml( config.toggleImageUrl ) + '" alt="" />';
			}
			if ( toggleStyle === 'ask_label' && config.markIconUrl ) {
				return '<span class="nam-aichat-toggle-label">Ask</span>' +
					'<img class="nam-aichat-toggle-mark" src="' +
					escapeHtml( config.markIconUrl ) + '" alt="" />';
			}
			var iconUrl = config.iconUrl || '';
			return '<img class="nam-aichat-toggle-icon" src="' +
				escapeHtml( iconUrl ) + '" alt="" width="28" height="28">';
		}

		// ----------------------------------------------------------
		// sessionStorage layer (floating only; inline never persists)
		// ----------------------------------------------------------

		function storageEnabled() {
			// Only the persistent footer widget restores across pages.
			return surface === 'floating';
		}

		function storageIdentityMatches( saved ) {
			return saved &&
				saved.version === STORAGE_VERSION &&
				saved.site === config.site &&
				saved.engine === config.engine &&
				saved.agentId === ( config.agentId || '' ) &&
				saved.isLoggedIn === !! config.isLoggedIn;
		}

		function loadStoredState() {
			if ( ! storageEnabled() ) { return null; }
			try {
				var raw = sessionStorage.getItem( STORAGE_KEY );
				if ( ! raw ) { return null; }
				var parsed = JSON.parse( raw );
				if ( ! storageIdentityMatches( parsed ) ) { return null; }
				return parsed;
			} catch ( e ) {
				return null;
			}
		}

		function clearStaleStateIfMismatched() {
			if ( ! storageEnabled() ) { return; }
			try {
				var raw = sessionStorage.getItem( STORAGE_KEY );
				if ( ! raw ) { return; }
				var parsed = JSON.parse( raw );
				if ( ! storageIdentityMatches( parsed ) ) {
					sessionStorage.removeItem( STORAGE_KEY );
				}
			} catch ( e ) {}
		}

		function saveStoredState( extras ) {
			if ( ! storageEnabled() ) { return; }
			try {
				var hist = ( ( extras && extras.history ) || [] ).slice( -MAX_HISTORY_MESSAGES );
				sessionStorage.setItem( STORAGE_KEY, JSON.stringify( {
					version:        STORAGE_VERSION,
					site:           config.site,
					engine:         config.engine,
					agentId:        config.agentId || '',
					isLoggedIn:     !! config.isLoggedIn,
					conversationId: ( extras && extras.conversationId ) || '',
					history:        hist,
					opened:         !! ( extras && extras.opened )
				} ) );
			} catch ( e ) {
				// Quota exceeded or storage disabled - silent no-op.
			}
		}

		function clearStoredState() {
			if ( ! storageEnabled() ) { return; }
			try { sessionStorage.removeItem( STORAGE_KEY ); } catch ( e ) {}
		}

		clearStaleStateIfMismatched();

		// ----------------------------------------------------------
		// Open / minimize / new-chat wiring (floating only)
		// ----------------------------------------------------------

		var state = {
			opened: isInlineShell ? true : false
		};

		// Keep the docked aside panel aligned over the archive aside
		// column. Set when the archive-aside engine wires it up.
		var alignAsidePanel = function () {};
		// Archive-aside only: whether the chat is expanded to the full
		// modal (overlaying header/footer) vs docked over the aside.
		var asideExpanded = false;

		function toggleAsideExpand() {
			var panel = root.querySelector( '.nam-aichat-panel' );
			var modal = root.querySelector( '.nam-aichat-modal' );
			var btn = root.querySelector( '.nam-aichat-expand' );
			if ( ! panel || ! modal ) { return; }
			asideExpanded = ! asideExpanded;
			panel.classList.toggle( 'is-expanded', asideExpanded );
			if ( asideExpanded ) {
				// Drop the docked geometry so the full-modal CSS takes over.
				modal.style.left = '';
				modal.style.top = '';
				modal.style.width = '';
				modal.style.height = '';
				modal.classList.remove( 'nam-aichat-modal--aside-fallback' );
			} else {
				alignAsidePanel();
			}
			if ( btn ) {
				var label = asideExpanded ? 'Collapse to side panel' : 'Expand to full screen';
				btn.setAttribute( 'aria-label', label );
				btn.setAttribute( 'title', label );
				btn.classList.toggle( 'is-expanded', asideExpanded );
			}
		}

		function openPanel( afterOpen ) {
			if ( isInlineShell ) {
				if ( typeof afterOpen === 'function' ) { setTimeout( afterOpen, 50 ); }
				return;
			}
			var panel = root.querySelector( '.nam-aichat-panel' );
			var toggleBtn = root.querySelector( '.nam-aichat-toggle' );
			panel.removeAttribute( 'hidden' );
			toggleBtn.setAttribute( 'aria-expanded', 'true' );
			document.body.classList.add( BODY_OPEN_CLASS );
			state.opened = true;
			alignAsidePanel();
			onPanelStateChanged();
			if ( typeof afterOpen === 'function' ) {
				setTimeout( afterOpen, 50 );
			}
		}

		function minimizePanel() {
			if ( isInlineShell ) { return; }
			var panel = root.querySelector( '.nam-aichat-panel' );
			var toggleBtn = root.querySelector( '.nam-aichat-toggle' );
			panel.setAttribute( 'hidden', '' );
			toggleBtn.setAttribute( 'aria-expanded', 'false' );
			document.body.classList.remove( BODY_OPEN_CLASS );
			state.opened = false;
			onPanelStateChanged();
		}

		function attachActionHandlers() {
			var newchatBtn = root.querySelector( '.nam-aichat-newchat' );
			if ( newchatBtn ) {
				newchatBtn.addEventListener( 'click', function () { onNewChat(); } );
			}

			if ( isInlineShell ) { return; }

			var toggleBtn = root.querySelector( '.nam-aichat-toggle' );
			var minimizeBtn = root.querySelector( '.nam-aichat-minimize' );
			var backdrop = root.querySelector( '.nam-aichat-backdrop' );

			toggleBtn.addEventListener( 'click', function () { openPanel( onPanelOpen ); } );
			minimizeBtn.addEventListener( 'click', minimizePanel );
			if ( backdrop ) {
				backdrop.addEventListener( 'click', minimizePanel );
			}

			var expandBtn = root.querySelector( '.nam-aichat-expand' );
			if ( expandBtn ) {
				expandBtn.addEventListener( 'click', toggleAsideExpand );
			}
			document.addEventListener( 'keydown', function ( e ) {
				if ( e.key === 'Escape' && state.opened ) {
					minimizePanel();
				}
			} );

			// Re-align the docked aside panel as the layout shifts.
			if ( isArchiveAside ) {
				window.addEventListener( 'resize', function () {
					if ( state.opened ) { alignAsidePanel(); }
				} );
				window.addEventListener( 'scroll', function () {
					if ( state.opened ) { alignAsidePanel(); }
				}, { passive: true } );
			}
		}

		// Position the open panel directly over the archive aside column
		// (.archive-listing__aside-panel / .archive__aside). Falls back to
		// the default docked-right CSS when the aside is not on screen
		// (e.g. below the responsive breakpoint where it is hidden).
		function setupAsideAlignment() {
			if ( ! isArchiveAside ) { return; }
			alignAsidePanel = function () {
				if ( asideExpanded ) { return; }
				var modal = root.querySelector( '.nam-aichat-modal' );
				if ( ! modal ) { return; }
				var aside = document.querySelector( '.archive-listing__aside-panel' ) ||
					document.querySelector( '.archive__aside.aside' );
				if ( ! aside ) {
					modal.classList.add( 'nam-aichat-modal--aside-fallback' );
					modal.style.left = '';
					modal.style.top = '';
					modal.style.width = '';
					modal.style.height = '';
					return;
				}
				var rect = aside.getBoundingClientRect();
				if ( rect.width < 1 || rect.height < 1 ) {
					modal.classList.add( 'nam-aichat-modal--aside-fallback' );
					return;
				}
				modal.classList.remove( 'nam-aichat-modal--aside-fallback' );
				var top = Math.max( 12, rect.top );
				var bottom = Math.min( window.innerHeight - 12, rect.bottom );
				modal.style.left = Math.round( rect.left ) + 'px';
				modal.style.top = Math.round( top ) + 'px';
				modal.style.width = Math.round( rect.width ) + 'px';
				modal.style.height = Math.round( Math.max( 320, bottom - top ) ) + 'px';
			};
		}

		// Engine sets these. Defaults are no-ops.
		var onPanelOpen = function () {};
		var onChipClick = function () {};
		var onPanelStateChanged = function () {};
		var onNewChat = function () {};

		setupAsideAlignment();

		// Animate `text` into an <input>/<textarea> character by character,
		// then run done(). Mimics a person typing the pre-typed prompt.
		function typeInto( el, text, done ) {
			var full = String( text || '' );
			if ( ! el || ! full ) { if ( typeof done === 'function' ) { done(); } return; }
			var i = 0;
			el.focus();
			var speed = full.length > 160 ? 28 : 50;
			var timer = setInterval( function () {
				i++;
				el.value = full.slice( 0, i );
				el.dispatchEvent( new Event( 'input', { bubbles: true } ) );
				if ( i >= full.length ) {
					clearInterval( timer );
					if ( typeof done === 'function' ) { setTimeout( done, 280 ); }
				}
			}, speed );
		}

		function loadSuggestions() {
			if ( ! config.suggestionsUrl ) { return; }
			fetch( config.suggestionsUrl, {
				credentials: 'same-origin',
				headers: { Accept: 'application/json' }
			} )
				.then( function ( r ) { return r.ok ? r.json() : { suggestions: [] }; } )
				.then( function ( json ) {
					var prompts = ( json && json.suggestions ) || [];
					renderSuggestions( prompts );
				} )
				.catch( function () { /* silent */ } );
		}

		function renderSuggestions( prompts ) {
			var sg = root.querySelector( '.nam-aichat-suggestions' );
			if ( ! sg || ! prompts || ! prompts.length ) { return; }
			sg.innerHTML = '';
			prompts.forEach( function ( p ) {
				var btn = document.createElement( 'button' );
				btn.type = 'button';
				btn.className = 'nam-aichat-suggestion';
				btn.textContent = p;
				btn.addEventListener( 'click', function () {
					onChipClick( p );
				} );
				sg.appendChild( btn );
			} );
		}

		// Shared chrome. Floating gets a toggle + modal; inline gets a
		// flat always-visible shell. Inner body is engine-specific.
		function buildPanelChrome( titleText, innerBodyHtml, includeNewChat ) {
			var newChatBtn = includeNewChat
				? '<button type="button" class="nam-aichat-action nam-aichat-newchat" aria-label="Start new chat" title="Start new chat">' +
					'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
						'<path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />' +
						'<polyline points="21 3 21 8 16 8" />' +
						'<path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />' +
						'<polyline points="3 21 3 16 8 16" />' +
					'</svg>' +
				'</button>'
				: '';

			if ( isInlineShell ) {
				return '' +
					'<div class="nam-aichat-inline-shell">' +
						'<div class="nam-aichat-header">' +
							'<span class="nam-aichat-title">' + escapeHtml( titleText ) + '</span>' +
							'<div class="nam-aichat-actions">' +
								newChatBtn +
							'</div>' +
						'</div>' +
						innerBodyHtml +
					'</div>';
			}

			var minimizeBtn =
				'<button type="button" class="nam-aichat-action nam-aichat-minimize" aria-label="Minimize" title="Minimize">' +
					'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true">' +
						'<line x1="5" y1="13" x2="19" y2="13" />' +
					'</svg>' +
				'</button>';

			// Archive-aside only: expand/collapse between the docked aside
			// panel and the full-screen modal (which overlays header/footer).
			var expandBtn = isArchiveAside
				? '<button type="button" class="nam-aichat-action nam-aichat-expand" aria-label="Expand to full screen" title="Expand to full screen">' +
						'<svg class="nam-aichat-icon-expand" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
							'<polyline points="15 3 21 3 21 9" />' +
							'<polyline points="9 21 3 21 3 15" />' +
							'<line x1="21" y1="3" x2="14" y2="10" />' +
							'<line x1="3" y1="21" x2="10" y2="14" />' +
						'</svg>' +
						'<svg class="nam-aichat-icon-collapse" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
							'<polyline points="4 14 10 14 10 20" />' +
							'<polyline points="20 10 14 10 14 4" />' +
							'<line x1="14" y1="10" x2="21" y2="3" />' +
							'<line x1="3" y1="21" x2="10" y2="14" />' +
						'</svg>' +
					'</button>'
				: '';

			var panelClass = 'nam-aichat-panel' + ( isArchiveAside ? ' nam-aichat-panel--aside' : '' );
			var modalClass = 'nam-aichat-modal' + ( isArchiveAside ? ' nam-aichat-modal--aside' : '' );

			return '' +
				'<button type="button" class="nam-aichat-toggle" aria-label="' + escapeHtml( titleText ) + '" aria-expanded="false">' +
					buildToggleInner() +
				'</button>' +
				'<div class="' + panelClass + '" hidden role="dialog" aria-modal="true" aria-label="' + escapeHtml( titleText ) + '">' +
					'<div class="nam-aichat-backdrop"></div>' +
					'<div class="' + modalClass + '">' +
						'<div class="nam-aichat-header">' +
							'<span class="nam-aichat-title">' + escapeHtml( titleText ) + '</span>' +
							'<div class="nam-aichat-actions">' +
								newChatBtn +
								expandBtn +
								minimizeBtn +
							'</div>' +
						'</div>' +
						innerBodyHtml +
					'</div>' +
				'</div>';
		}

		// ----------------------------------------------------------
		// Engine: Algolia (custom chat UI streaming from Agent Studio)
		// ----------------------------------------------------------

		function runAlgoliaEngine() {
			var algoliaState = {
				conversationId: null,
				history: [],
				sending: false,
				restored: false
			};

			var title = config.title || 'Ask AI';
			var placeholder = 'Ask a question…';
			var disclaimer = 'Beta. Verify answers against source pages.';

			var bodyHtml =
				'<div class="nam-aichat-messages" aria-live="polite" aria-relevant="additions text"></div>' +
				'<div class="nam-aichat-suggestions" aria-label="Suggested questions"></div>' +
				'<div class="nam-aichat-status" role="status" aria-live="polite"></div>' +
				'<form class="nam-aichat-form" autocomplete="off">' +
					'<input type="text" class="nam-aichat-input" placeholder="' + escapeHtml( placeholder ) + '">' +
					'<button type="submit" class="nam-aichat-send" aria-label="Send">' +
						'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12 L21 4 L13 21 L11 13 Z"/></svg>' +
					'</button>' +
				'</form>' +
				'<div class="nam-aichat-disclaimer">' + escapeHtml( disclaimer ) + '</div>';

			root.innerHTML = buildPanelChrome( title, bodyHtml, true );

			attachActionHandlers();

			var form = root.querySelector( '.nam-aichat-form' );
			var input = root.querySelector( '.nam-aichat-input' );
			form.addEventListener( 'submit', function ( e ) {
				e.preventDefault();
				var text = input.value;
				input.value = '';
				send( text );
			} );

			function seedConversation() {
				if ( algoliaState.conversationId || algoliaState.restored ) { return; }
				algoliaState.conversationId = genId();
				renderWelcome();
				loadSuggestions();
			}

			// Type the editor prompt into the input, then submit it so the
			// answer is generated automatically. Runs at most once.
			var prefillDone = false;
			function runPrefill() {
				if ( prefillDone || ! prefillPrompt ) { return; }
				prefillDone = true;
				seedConversation();
				typeInto( input, prefillPrompt, function () {
					input.value = '';
					send( prefillPrompt );
				} );
			}

			// Wire generic action callbacks to algolia-specific behavior.
			onPanelOpen = function () {
				input.focus();
				seedConversation();
			};
			onChipClick = function ( text ) { send( text ); };
			onNewChat = function () { newChat(); };
			onPanelStateChanged = function () { persist(); };

			// Persist + restore (floating only).
			var stored = loadStoredState();
			if ( stored && stored.history && stored.history.length ) {
				algoliaState.conversationId = stored.conversationId || genId();
				algoliaState.history = stored.history;
				algoliaState.restored = true;
				repaintHistory( stored.history );
				if ( stored.opened ) {
					openPanel(); // skip onPanelOpen seeding (state.restored guards it)
				}
			}

			window.addEventListener( 'pagehide', persist );

			if ( isInlineShell ) {
				// Always-visible shell: seed + type the prompt on load.
				seedConversation();
				runPrefill();
			} else if ( isArchiveAside ) {
				// Start minimized, then self-unfurl over the aside and type
				// + submit the prompt once the panel has opened.
				setTimeout( function () {
					openPanel( function () {
						seedConversation();
						runPrefill();
					} );
				}, 850 );
			}

			function persist() {
				saveStoredState( {
					conversationId: algoliaState.conversationId,
					history:        algoliaState.history,
					opened:         state.opened
				} );
			}

			function renderWelcome() {
				var msgs = root.querySelector( '.nam-aichat-messages' );
				msgs.innerHTML = '<div class="nam-aichat-msg nam-aichat-welcome">What can I help you find today?</div>';
			}

			// Walk the saved history and paint visible turns into the
			// messages list. Skips the hidden context preface and its
			// synthetic assistant ack.
			function repaintHistory( history ) {
				var msgs = root.querySelector( '.nam-aichat-messages' );
				if ( ! msgs ) { return; }
				msgs.innerHTML = '';
				for ( var i = 0; i < history.length; i++ ) {
					var turn = history[ i ];
					var text = ( turn && turn.parts && turn.parts[ 0 ] && turn.parts[ 0 ].text ) || '';
					if ( ! text ) { continue; }
					if ( text.indexOf( '[Context for this conversation' ) === 0 ) { continue; }
					if ( text === 'Understood. I will keep that page context in mind.' ) { continue; }
					if ( turn.role === 'user' ) {
						appendUserMessage( text );
					} else if ( turn.role === 'assistant' ) {
						appendAssistantMessage( text );
					}
				}
			}

			function newChat() {
				algoliaState.history = [];
				algoliaState.conversationId = genId();
				algoliaState.sending = false;
				algoliaState.restored = false;
				var msgs = root.querySelector( '.nam-aichat-messages' );
				if ( msgs ) { msgs.innerHTML = ''; }
				var sg = root.querySelector( '.nam-aichat-suggestions' );
				if ( sg ) { sg.innerHTML = ''; }
				renderWelcome();
				loadSuggestions();
				clearStoredState();
			}

			function setStatus( text ) {
				var el = root.querySelector( '.nam-aichat-status' );
				if ( el ) { el.textContent = text || ''; }
			}

			function appendUserMessage( text ) {
				var msgs = root.querySelector( '.nam-aichat-messages' );
				var node = document.createElement( 'div' );
				node.className = 'nam-aichat-msg nam-aichat-user';
				node.innerHTML = '<div class="nam-aichat-bubble">' + escapeHtml( text ).replace( /\n/g, '<br>' ) + '</div>';
				msgs.appendChild( node );
				return node;
			}

			function appendAssistantMessage( text ) {
				var msgs = root.querySelector( '.nam-aichat-messages' );
				var node = document.createElement( 'div' );
				node.className = 'nam-aichat-msg nam-aichat-assistant';
				node.innerHTML = '<div class="nam-aichat-bubble"><div class="nam-aichat-answer">' + formatAssistantText( text ) + '</div></div>';
				msgs.appendChild( node );
			}

			function appendThinking() {
				var msgs = root.querySelector( '.nam-aichat-messages' );
				var node = document.createElement( 'div' );
				node.className = 'nam-aichat-msg nam-aichat-assistant nam-aichat-thinking';
				node.innerHTML = '<div class="nam-aichat-bubble"><span class="nam-aichat-dots"><span></span><span></span><span></span></span></div>';
				msgs.appendChild( node );
				return node;
			}

			// Scroll so the given message sits near the top of the messages
			// area. The answer then streams in below it and the reader scrolls
			// down at their own pace (we do not auto-follow to the bottom).
			function scrollMessageToTop( node ) {
				var msgs = root.querySelector( '.nam-aichat-messages' );
				if ( ! msgs || ! node ) { return; }
				var top = node.getBoundingClientRect().top -
					msgs.getBoundingClientRect().top + msgs.scrollTop;
				msgs.scrollTop = Math.max( 0, top - 8 );
			}

			// Today's date (site timezone from the server payload, else the
			// browser's). Lets the model judge "latest" / "upcoming" even when
			// the agent's date_aware feature is off.
			function todayIso() {
				if ( config.today && /^\d{4}-\d{2}-\d{2}$/.test( config.today ) ) { return config.today; }
				var d = new Date();
				var m = String( d.getMonth() + 1 ), day = String( d.getDate() );
				return d.getFullYear() + '-' + ( m.length < 2 ? '0' + m : m ) + '-' + ( day.length < 2 ? '0' + day : day );
			}

			function buildSystemPreface() {
				var parts = [];
				parts.push( '[Current date: ' + todayIso() + '] Today is ' + todayIso() + '. Treat content published long before this date as old; prefer and date your sources accordingly.' );
				// Keep answers readable in the chat UI: Markdown/plain prose,
				// never raw HTML, and hyperlink descriptive text rather than
				// printing bare URLs.
				parts.push( 'Formatting: respond in Markdown or plain prose (never raw HTML or full HTML documents). When you reference a page or source, hyperlink the descriptive text inline using Markdown link syntax [anchor text](https://full-url) - for example [Attract the Workforce of Today](https://example.org/attract-your-workforce). Do NOT print bare or angle-bracket URLs, and do NOT show the raw URL separately from its link text.' );

				// Sourcing: answers must come from across the whole site (all
				// content the user is permitted to access), NOT just the page
				// the user happens to be on. The current page is not sent as
				// context for answers at all - it only drives the suggested
				// starter prompts (generated server-side).
				parts.push( 'Sourcing: always use your search tool to answer from across all site content the user is permitted to access. Never limit your answer to the current page, and never reply that information "was not provided" or "is not in the context" - search the site for it before answering.' );

				if ( config.gate === 'post_type' ) {
					if ( config.isLoggedIn ) {
						parts.push( 'The user IS a logged-in member of this organization with full access to all resources, including the members-only library. You may freely cite and summarize members-only material when relevant.' );
					} else {
						parts.push( 'The user is browsing as a guest (not logged in). Members-only resources have been filtered from your search results - you literally cannot see them. Do not pretend or speculate about their contents.' );
					}
				}

				if ( config.teaser && config.teaser.message ) {
					var ctaLines = ( config.teaser.ctas || [] ).map( function ( c ) {
						return '- ' + c.label + ': ' + c.url;
					} ).join( '\n' );
					parts.push(
						'\n\n=== SYSTEM_INSTRUCTION ===\n' +
						'After you finish answering the user, ' +
						'append exactly the following acknowledgement at the end of your response, ' +
						'preserving the wording and including the call-to-action links:\n\n' +
						config.teaser.message +
						( ctaLines ? '\n\n' + ctaLines : '' ) +
						'\n=== END_SYSTEM_INSTRUCTION ==='
					);
				}

				return parts.length ? parts.join( ' ' ) : '';
			}

			function send( text ) {
				var trimmed = String( text || '' ).trim();
				if ( ! trimmed || algoliaState.sending ) { return; }
				algoliaState.sending = true;

				var sg = root.querySelector( '.nam-aichat-suggestions' );
				if ( sg ) { sg.innerHTML = ''; }

				// First-time conversation: drop the welcome line and
				// inject the hidden context preface.
				if ( algoliaState.history.length === 0 ) {
					var msgs = root.querySelector( '.nam-aichat-messages' );
					if ( msgs ) { msgs.innerHTML = ''; }
					var preface = buildSystemPreface();
					if ( preface ) {
						algoliaState.history.push( {
							id: genId(),
							role: 'user',
							parts: [ { type: 'text', text: '[Context for this conversation, do not echo back] ' + preface } ]
						} );
						algoliaState.history.push( {
							id: genId(),
							role: 'assistant',
							parts: [ { type: 'text', text: 'Understood. I will keep that page context in mind.' } ]
						} );
					}
				}

				var userNode = appendUserMessage( trimmed );
				algoliaState.history.push( { id: genId(), role: 'user', parts: [ { type: 'text', text: trimmed } ] } );
				persist();

				var thinkingNode = appendThinking();
				// Pin the question near the top; let the answer fill below it.
				scrollMessageToTop( userNode );
				var bubbleNode = null;
				var answerEl = null;

				streamChat( algoliaState.conversationId, trimHistoryForRequest( algoliaState.history ), {
					onDelta: function ( fullText ) {
						if ( ! bubbleNode ) {
							bubbleNode = document.createElement( 'div' );
							bubbleNode.className = 'nam-aichat-msg nam-aichat-assistant';
							bubbleNode.innerHTML = '<div class="nam-aichat-bubble"><div class="nam-aichat-answer"></div></div>';
							answerEl = bubbleNode.querySelector( '.nam-aichat-answer' );
							thinkingNode.replaceWith( bubbleNode );
						}
						answerEl.innerHTML = formatAssistantText( fullText );
						// Intentionally do not auto-scroll while streaming so
						// the reader stays where they are and scrolls down to
						// read more at their own pace.
					},
					onStatus: setStatus,
					onError: function ( msg ) {
						if ( thinkingNode && thinkingNode.parentNode ) { thinkingNode.remove(); }
						if ( bubbleNode && bubbleNode.parentNode ) { bubbleNode.remove(); }
						setStatus( msg || 'Something went wrong.' );
					}
				} ).then( function ( result ) {
					if ( ! bubbleNode ) {
						if ( thinkingNode && thinkingNode.parentNode ) { thinkingNode.remove(); }
						var fallback = document.createElement( 'div' );
						fallback.className = 'nam-aichat-msg nam-aichat-assistant';
						fallback.innerHTML = '<div class="nam-aichat-bubble"><em>No response received.</em></div>';
						root.querySelector( '.nam-aichat-messages' ).appendChild( fallback );
					}
					algoliaState.history.push( { id: genId(), role: 'assistant', parts: [ { type: 'text', text: ( result && result.text ) || '' } ] } );
					setStatus( '' );
					algoliaState.sending = false;
					persist();
				} ).catch( function ( err ) {
					if ( thinkingNode && thinkingNode.parentNode ) { thinkingNode.remove(); }
					if ( bubbleNode && bubbleNode.parentNode ) { bubbleNode.remove(); }
					setStatus( ( err && err.message ) || 'Something went wrong.' );
					algoliaState.sending = false;
					persist();
				} );
			}

			// Keep the hidden preface (+ its ack) and only the most recent
			// visible turns so long sessions don't anchor new questions on
			// stale answers (and stay under the model's context budget).
			var MAX_REQUEST_TURNS = 12;
			function trimHistoryForRequest( history ) {
				if ( ! history || history.length <= MAX_REQUEST_TURNS + 2 ) { return history; }
				var head = [];
				var first = history[ 0 ] && history[ 0 ].parts && history[ 0 ].parts[ 0 ] && history[ 0 ].parts[ 0 ].text || '';
				if ( first.indexOf( '[Context for this conversation' ) === 0 ) {
					head = history.slice( 0, 2 );
				}
				return head.concat( history.slice( -MAX_REQUEST_TURNS ) );
			}

			function streamChat( conversationId, messages, callbacks ) {
				// cache=false: Agent Studio caches first-message completions for
				// up to 90 days by default; a chat about "the latest ..." must
				// never be served from that cache.
				var url = 'https://' + encodeURIComponent( config.algolia.appId ) +
					'.algolia.net/agent-studio/1/agents/' + encodeURIComponent( config.agentId ) +
					'/completions?stream=true&compatibilityMode=ai-sdk-5&cache=false';
				var body = { messages: messages };
				if ( conversationId ) { body.id = conversationId; }
				// Per-request search parameters (recency optionalFilters, keyed
				// by index) computed server-side in functions/ai-chatbot.php.
				var sp = config.algolia && config.algolia.searchParameters;
				if ( sp && typeof sp === 'object' && Object.keys( sp ).length ) {
					body.algolia = { searchParameters: sp };
				}
				callbacks.onStatus( 'Connecting…' );

				return fetch( url, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'x-algolia-application-id': config.algolia.appId,
						'x-algolia-api-key': config.algolia.searchKey
					},
					body: JSON.stringify( body )
				} ).then( function ( res ) {
					if ( ! res.ok ) {
						return res.text().then( function ( txt ) {
							var msg = 'Algolia returned HTTP ' + res.status;
							try {
								var json = JSON.parse( txt );
								msg = json.error || json.message || msg;
							} catch ( _ ) {}
							throw new Error( msg );
						} );
					}
					callbacks.onStatus( 'Thinking…' );
					return readSseStream( res, callbacks );
				} );
			}

			function readSseStream( res, callbacks ) {
				var reader = res.body.getReader();
				var decoder = new TextDecoder();
				var buffer = '';
				var fullText = '';
				var firstDelta = false;

				function pump() {
					return reader.read().then( function ( result ) {
						if ( result.done ) { return { text: fullText }; }
						buffer += decoder.decode( result.value, { stream: true } );
						var lines = buffer.split( '\n' );
						buffer = lines.pop() || '';
						for ( var i = 0; i < lines.length; i++ ) {
							var line = lines[ i ].replace( /\s+$/, '' );
							if ( ! line || line.charAt( 0 ) === ':' ) { continue; }
							var payload = line;
							if ( line.indexOf( 'data: ' ) === 0 ) {
								payload = line.slice( 6 );
							} else if ( line.indexOf( 'data:' ) === 0 ) {
								payload = line.slice( 5 );
							}
							payload = payload.trim();
							if ( ! payload || payload === '[DONE]' || payload.charAt( 0 ) !== '{' ) { continue; }
							var obj;
							try { obj = JSON.parse( payload ); } catch ( _ ) { continue; }
							if ( ! obj || ! obj.type ) { continue; }

							switch ( obj.type ) {
								case 'start':
								case 'start-step':
									callbacks.onStatus( 'Thinking…' );
									break;
								case 'tool-input-start':
								case 'tool-input-delta':
								case 'tool-input-available':
								case 'tool-call':
									callbacks.onStatus( 'Searching…' );
									break;
								case 'tool-output-available':
								case 'tool-result':
									callbacks.onStatus( 'Generating answer…' );
									break;
								case 'reasoning-start':
								case 'reasoning-delta':
								case 'reasoning-end':
									if ( ! firstDelta ) { callbacks.onStatus( 'Thinking…' ); }
									break;
								case 'text-delta':
									if ( typeof obj.delta === 'string' ) {
										if ( ! firstDelta ) { firstDelta = true; callbacks.onStatus( '' ); }
										fullText += obj.delta;
										callbacks.onDelta( fullText );
									}
									break;
								case 'text':
									if ( typeof obj.text === 'string' ) {
										if ( ! firstDelta ) { firstDelta = true; callbacks.onStatus( '' ); }
										fullText += obj.text;
										callbacks.onDelta( fullText );
									}
									break;
								case 'error':
									callbacks.onError( obj.errorText || obj.message || obj.error || 'An error occurred.' );
									break;
							}
						}
						return pump();
					} );
				}

				return pump();
			}
		}

		// ----------------------------------------------------------
		// Engine: Betty (Tasio <betty-bot> web component on IRI)
		// ----------------------------------------------------------

		function runBettyEngine() {
			var title = config.title || 'IRIS';
			var betty = config.betty || {};
			var disclaimer = 'Beta. Verify answers against source pages.';
			var infoHtml = '<h3>What can I help you find today?</h3>';

			var bodyHtml =
				'<div class="nam-aichat-suggestions" aria-label="Suggested questions"></div>' +
				'<div class="nam-aichat-betty-host"></div>' +
				'<div class="nam-aichat-disclaimer">' + escapeHtml( disclaimer ) + '</div>';

			root.innerHTML = buildPanelChrome( title, bodyHtml, true );

			var host = root.querySelector( '.nam-aichat-betty-host' );

			function mountBettyBot() {
				// Wipe any previous bot to ensure a fresh shadow DOM.
				while ( host.firstChild ) { host.removeChild( host.firstChild ); }
				var bot = document.createElement( 'betty-bot' );
				bot.className = 'nam-aichat-betty-bot';
				bot.setAttribute( 'api-url', betty.apiUrl );
				bot.setAttribute( 'api-endpoint-token', betty.apiToken );
				bot.setAttribute( 'stream', '1' );
				bot.setAttribute( 'user-id', '' );
				bot.setAttribute( 'info-html', infoHtml );
				if ( betty.assistantImage ) { bot.setAttribute( 'assistant-image', betty.assistantImage ); }
				if ( betty.userImage ) { bot.setAttribute( 'user-image', betty.userImage ); }
				if ( betty.termsUrl ) { bot.setAttribute( 'terms-url', betty.termsUrl ); }
				host.appendChild( bot );
				startIconSwap( bot );
				return bot;
			}

			function startIconSwap( bot ) {
				if ( ! betty.assistantImage ) { return; }
				var attempts = 0;
				var iconTimer = setInterval( function () {
					attempts++;
					var roots = [ bot, bot.shadowRoot ].filter( Boolean );
					roots.forEach( function ( r ) {
						var imgs = r.querySelectorAll ? r.querySelectorAll( 'img' ) : [];
						for ( var i = 0; i < imgs.length; i++ ) {
							var src = imgs[ i ].getAttribute( 'src' ) || '';
							if ( src.indexOf( 'BettyOnBetty-assistant.svg' ) !== -1 ) {
								imgs[ i ].src = betty.assistantImage;
							}
						}
					} );
					if ( bot.shadowRoot || attempts > 60 ) {
						clearInterval( iconTimer );
					}
				}, 250 );
			}

			var currentBot = mountBettyBot();
			attachActionHandlers();

			var bettyState = {
				suggestionsLoaded: false
			};

			// Restore panel-open flag (floating only).
			var stored = loadStoredState();
			if ( stored && stored.opened ) {
				openPanel();
			}

			window.addEventListener( 'pagehide', persist );

			function persist() {
				saveStoredState( {
					conversationId: '',
					history:        [],
					opened:         state.opened
				} );
			}

			// Wire the generic action callbacks to Betty-specific behavior.
			onPanelOpen = function () {
				if ( ! bettyState.suggestionsLoaded ) {
					bettyState.suggestionsLoaded = true;
					loadSuggestions();
				}
			};
			onChipClick = function ( text ) { tryInject( text, false ); };
			onPanelStateChanged = function () { persist(); };
			onNewChat = function () {
				currentBot = mountBettyBot();
				var sg = root.querySelector( '.nam-aichat-suggestions' );
				if ( sg ) { sg.innerHTML = ''; }
				bettyState.suggestionsLoaded = false;
				loadSuggestions();
				bettyState.suggestionsLoaded = true;
			};

			// Type the editor prompt into Betty's textarea, then submit it.
			// Runs at most once.
			var prefillDone = false;
			function runPrefill() {
				if ( prefillDone || ! prefillPrompt ) { return; }
				prefillDone = true;
				typeAndSubmitBetty( prefillPrompt );
			}

			if ( isInlineShell ) {
				// Always-visible shell: load chips + type/submit on load.
				bettyState.suggestionsLoaded = true;
				loadSuggestions();
				runPrefill();
			} else if ( isArchiveAside ) {
				// Start minimized, then self-unfurl and type + submit.
				setTimeout( function () {
					openPanel( function () {
						if ( ! bettyState.suggestionsLoaded ) {
							bettyState.suggestionsLoaded = true;
							loadSuggestions();
						}
						runPrefill();
					} );
				}, 850 );
			}

			function tryInject( text, fillOnly ) {
				var trimmed = String( text || '' ).trim();
				if ( ! trimmed ) { return; }
				if ( ! fillOnly ) {
					var sg = root.querySelector( '.nam-aichat-suggestions' );
					if ( sg ) { sg.innerHTML = ''; }
				}
				var attempts = 0;
				var timer = setInterval( function () {
					attempts++;
					if ( injectOnce( trimmed, fillOnly ) || attempts > 60 ) {
						clearInterval( timer );
					}
				}, 100 );
			}

			// Wait for Betty's shadow-DOM textarea, animate the prompt into
			// it character by character, then click send.
			function typeAndSubmitBetty( text ) {
				var trimmed = String( text || '' ).trim();
				if ( ! trimmed ) { return; }
				var sg = root.querySelector( '.nam-aichat-suggestions' );
				if ( sg ) { sg.innerHTML = ''; }
				var attempts = 0;
				var waiter = setInterval( function () {
					attempts++;
					var els = getBettyEls();
					if ( els.textarea && els.btn ) {
						clearInterval( waiter );
						typeIntoBetty( els.textarea, trimmed, function () {
							var fresh = getBettyEls();
							if ( fresh.btn ) { fresh.btn.click(); }
						} );
					} else if ( attempts > 80 ) {
						clearInterval( waiter );
					}
				}, 100 );
			}

			function typeIntoBetty( textarea, text, done ) {
				var full = String( text || '' );
				var i = 0;
				var speed = full.length > 160 ? 28 : 50;
				var timer = setInterval( function () {
					i++;
					textarea.value = full.slice( 0, i );
					textarea.dispatchEvent( new Event( 'input', { bubbles: true } ) );
					textarea.dispatchEvent( new Event( 'change', { bubbles: true } ) );
					if ( i >= full.length ) {
						clearInterval( timer );
						if ( typeof done === 'function' ) { setTimeout( done, 280 ); }
					}
				}, speed );
			}

			function getBettyEls() {
				var bot = currentBot;
				var qTextarea = '#input-group textarea#input, #input-group textarea[name="Input"], textarea#input';
				var textarea = bot.shadowRoot
					? bot.shadowRoot.querySelector( qTextarea )
					: document.querySelector( qTextarea );
				var btn = bot.shadowRoot
					? bot.shadowRoot.querySelector( '#btnSend' )
					: document.querySelector( '#btnSend' );
				return { textarea: textarea, btn: btn };
			}

			function injectOnce( text, fillOnly ) {
				var els = getBettyEls();
				var textarea = els.textarea;
				var btn = els.btn;
				if ( ! textarea || ( ! fillOnly && ! btn ) ) { return false; }

				textarea.value = text;
				textarea.dispatchEvent( new Event( 'input', { bubbles: true } ) );
				textarea.dispatchEvent( new Event( 'change', { bubbles: true } ) );
				if ( ! fillOnly && btn ) {
					btn.click();
				}
				return true;
			}
		}

		// Boot the selected engine for this instance.
		if ( engine === 'betty' ) {
			runBettyEngine();
		} else {
			runAlgoliaEngine();
		}
	}

	// --------------------------------------------------------------
	// Discover + boot all surfaces on the page
	// --------------------------------------------------------------

	function parseConfig( raw ) {
		if ( ! raw ) { return null; }
		try {
			return JSON.parse( raw );
		} catch ( e ) {
			console.error( '[ai-chatbot] could not parse data-config', e );
			return null;
		}
	}

	function init() {
		// Floating widget (wp_footer).
		var floating = document.getElementById( FLOATING_ROOT_ID );
		if ( floating && floating.dataset && floating.dataset.config ) {
			var floatingConfig = parseConfig( floating.dataset.config );
			if ( floatingConfig ) {
				bootInstance( floating, floatingConfig, { surface: 'floating' } );
			}
		}

		// AI Chatbot blocks in page content.
		var inlineEls = document.querySelectorAll( INLINE_SELECTOR );
		for ( var i = 0; i < inlineEls.length; i++ ) {
			var container = inlineEls[ i ];
			if ( ! container.dataset || ! container.dataset.config ) { continue; }
			if ( container.getAttribute( 'data-ai-booted' ) === '1' ) { continue; }
			var inlineConfig = parseConfig( container.dataset.config );
			if ( ! inlineConfig ) { continue; }
			var prompt = container.dataset.prompt || '';
			var surface = inlineConfig.surface || 'inline';
			container.setAttribute( 'data-ai-booted', '1' );

			if ( surface === 'archive-aside' ) {
				// Spawn a body-level host so the fixed bottom-right widget
				// is positioned relative to the viewport (not a transformed
				// content ancestor). The in-content block collapses away.
				container.style.display = 'none';
				var host = document.createElement( 'div' );
				host.className = 'nam-ai-chatbot-archive-host';
				document.body.appendChild( host );
				bootInstance( host, inlineConfig, {
					surface: 'archive-aside',
					prompt: prompt
				} );
			} else {
				var mount = container.querySelector( INLINE_MOUNT_SELECTOR ) || container;
				bootInstance( mount, inlineConfig, {
					surface: 'inline',
					prompt: prompt
				} );
			}
		}
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
})();
